import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, ListChecks, CalendarDays, AlertTriangle, Database,
  BarChart3, ReceiptText, History, Users, Search, LogOut, Menu, X, ShieldCheck,
  Building2, Settings, FileText, UploadCloud
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { fmtDate, fmtINR, daysLabel } from "../lib/format";

// perm = required permission (via can); anyPerm = any of; role = exact role required.
const NAV = [
  { to: "/customers", label: "Customers", icon: Building2, perm: "view" },
  { to: "/", label: "Action Dashboard", icon: LayoutDashboard, perm: "view" },
  { to: "/action-required", label: "Action Required", icon: ListChecks, perm: "view" },
  { to: "/calendar", label: "Renewal Calendar", icon: CalendarDays, perm: "view" },
  { to: "/ill-payments", label: "ILL / Postpaid", icon: ReceiptText, perm: "view" },
  { to: "/invoices", label: "Invoices", icon: FileText, perm: "view" },
  { to: "/upload-bills", label: "Upload Bills", icon: UploadCloud, perm: "upload_bills" },
  { to: "/missed-renewals", label: "Missed Renewals", icon: AlertTriangle, perm: "view" },
  { to: "/data-quality", label: "Data Quality", icon: Database, perm: "view" },
  { to: "/management", label: "Management", icon: BarChart3, perm: "view_reports" },
  { to: "/history", label: "Payment History", icon: History, perm: "view" },
  { to: "/settings", label: "Settings", icon: Settings, anyPerm: ["edit", "manage_users"] },
  // Masters (legacy) is intentionally NOT in the nav — it showed the same records across
  // several tabs (Customers/Locations/Connections) which read as duplication. The /masters
  // route still works for Super Admin via a direct URL as a fallback during rollout.
];

export default function Layout({ children }) {
  const { user, logout, can } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const boxRef = useRef();

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setResults(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!q || q.length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setResults(data); } catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const items = NAV.filter((n) => {
    if (n.role) return user?.role === n.role;
    if (n.anyPerm) return n.anyPerm.some((p) => can(p));
    return can(n.perm);
  });

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      {/* Sidebar */}
      <aside className={`fixed z-40 top-0 left-0 h-full w-64 bg-[#0F1B3D] text-slate-200 transform transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-16 flex items-center gap-2 px-5 border-b border-white/10">
          <div className="w-9 h-9 rounded-lg bg-blue-500 flex items-center justify-center font-head font-extrabold text-white">N</div>
          <div>
            <div className="font-head font-extrabold text-white leading-none">Nextra Pay</div>
            <div className="text-[10px] uppercase tracking-wider text-blue-300">Nextra Communications</div>
          </div>
        </div>
        <nav className="p-3 space-y-1 overflow-y-auto" style={{ height: "calc(100% - 4rem)" }}>
          {items.map((n) => {
            const Icon = n.icon;
            const active = loc.pathname === n.to;
            return (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${active ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-white/5"}`}>
                <Icon size={18} /> {n.label}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        <header className="h-16 sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200 flex items-center gap-3 px-4 lg:px-6">
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="toggle-sidebar">
            {open ? <X /> : <Menu />}
          </button>
          <div className="relative flex-1 max-w-xl" ref={boxRef}>
            <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="global-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search customer, vendor, account ID, mobile…"
              className="w-full pl-10 pr-4 h-10 rounded-lg bg-slate-100 border border-transparent focus:bg-white focus:border-blue-400 outline-none text-sm"
            />
            {results && (
              <div className="absolute mt-2 w-full bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden max-h-96 overflow-y-auto" data-testid="search-results">
                {results.circuits?.length === 0 && results.customers?.length === 0 && (
                  <div className="p-4 text-sm text-slate-400">No matches</div>
                )}
                {results.circuits?.map((c) => (
                  <button key={c.id} onClick={() => { nav(`/circuit/${c.id}`); setResults(null); setQ(""); }}
                    className="w-full text-left px-4 py-2.5 hover:bg-slate-50 border-b border-slate-100">
                    <div className="text-sm font-semibold">{c.customer_name} <span className="text-slate-400 font-mono-num text-xs">{c.internal_circuit_id}</span></div>
                    <div className="text-xs text-slate-500">{c.vendor_name} · {c.service_type} · {c.city || c.location_name || "—"} · exp {fmtDate(c.next_action_date)}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold leading-none">{user?.name}</div>
              <div className="text-[11px] text-slate-500 flex items-center gap-1 justify-end mt-0.5"><ShieldCheck size={12} /> {user?.role?.replace(/_/g, " ")}</div>
            </div>
            <button onClick={logout} data-testid="logout-btn" className="p-2 rounded-lg hover:bg-slate-100 text-slate-600" title="Logout">
              <LogOut size={18} />
            </button>
          </div>
        </header>
        {open && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setOpen(false)} />}
        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
